import { isDestroying, isDestroyed } from '@ember/destroyable'
import type { ApolloQueryResult } from '@apollo/client/core'
import {
  type BundleAvailabilityResponse,
  type BundleAvailabilityVariables,
  fetchBundleAvailability
} from 'customer-flows/gql/queries/bundle-availability'
import { isAvailableStatus, isBookableStatus } from 'customer-flows/availability/status'
import { type KeyRequest, QueryCache } from 'customer-flows/utils/core/query-cache'
import Logger from 'customer-flows/utils/logger'

export type BundleAvailabilityArgs = {
  quoteId?: string
  bundleId: string
}

export type BundleAvailabilityState = 'idle' | 'loading' | 'ready' | 'error'

export type BundleAvailabilityConfig = {
  staleTime?: number
  gcTime?: number
  maxEntries?: number
  retryBaseDelay?: number
  fetchFn?: typeof fetchBundleAvailability
}

const MAX_AUTOMATIC_RETRIES = 3

// Only the quote and the bundle are asked. The BE reads the main booking from the quote, so the
// answer also depends on that booking: the caller invalidates when it changes.
export function bundleAvailabilityVariables({ quoteId, bundleId }: BundleAvailabilityArgs): BundleAvailabilityVariables | undefined {
  if (!quoteId) return undefined

  return { quoteId, bundleId }
}

export function bundleAvailabilityKey(variables: BundleAvailabilityVariables): string {
  return JSON.stringify(variables)
}

export function indexByBundleItem(items: BundleItemAvailability[]): Map<string, BundleItemAvailability> {
  return new Map(items.map((item) => [item.bundleItemId, item]))
}

// Whether the customer can pick the item: at least one of its times is available and bookable.
export function hasSelectableTime(item: BundleItemAvailability | undefined): boolean {
  return (item?.availabilityDates ?? []).some((date) =>
    date.availabilityTimes.some(({ status }) => isAvailableStatus(status) && isBookableStatus(status))
  )
}

function extractItems(response: ApolloQueryResult<BundleAvailabilityResponse> | undefined): BundleItemAvailability[] {
  const result = response?.data?.bundleAvailability

  if (result?.__typename === 'BundleAvailabilitySuccess') return result.bundleItemsAvailability

  throw new Error(`Failed to fetch bundle availability: ${result?.__typename ?? 'no response'}`)
}

// The availability of the items of a bundle for a quote, behind a `QueryCache`. One entry per
// quote and bundle holds the verbatim BE response. Reading `state`, `items` or `getItem` is what
// fetches (stale-while-revalidate, deduped); writing is left to the cache.
export function createBundleAvailability(ctx: object, args: () => BundleAvailabilityArgs, config: BundleAvailabilityConfig = {}) {
  // args() reads other stores, which throws once the owner is torn down.
  const alive = () => !isDestroying(ctx) && !isDestroyed(ctx)

  const fetchFn = config.fetchFn ?? fetchBundleAvailability

  function currentRequest(): KeyRequest<BundleAvailabilityVariables> | undefined {
    if (!alive()) return undefined

    const variables = bundleAvailabilityVariables(args())

    return variables && { key: bundleAvailabilityKey(variables), dims: variables }
  }

  const cache: QueryCache<BundleAvailabilityVariables, BundleItemAvailability[]> = new QueryCache(ctx, {
    fetch: async (variables) => extractItems(await fetchFn(ctx, variables)),
    hasData: (items) => items.length > 0,
    emptyData: () => [],
    staleTime: config.staleTime,
    gcTime: config.gcTime,
    maxEntries: config.maxEntries,
    retryBaseDelay: config.retryBaseDelay,
    onSettled: (_key, variables, error) => {
      if (error) Logger.error('Failed to fetch bundle availability', { error, ...variables })
    },
    // Retries only fire for pinned keys, so unpinning ends them
    pinnedKeys: (): Set<string> => {
      const request = currentRequest()

      return new Set(request && cache.failureCount(request.key) < MAX_AUTOMATIC_RETRIES ? [request.key] : [])
    }
  })

  // The cache fetches on a microtask, so reading never writes tracked state during render
  function read() {
    const request = currentRequest()
    if (!request) return undefined

    cache.ensure([request])
    cache.touch([request.key])

    return { request, entry: cache.read(request.key) }
  }

  function getItems(): BundleItemAvailability[] {
    const current = read()

    return (current && cache.peek(current.request.key)) || []
  }

  return {
    get state(): BundleAvailabilityState {
      const current = read()
      if (!current) return 'idle'

      if (cache.peek(current.request.key) !== undefined) return 'ready'

      // A background retry keeps the error on screen, so the retry button stays
      if (current.entry?.status === 'error' || cache.failureCount(current.request.key) > 0) return 'error'

      return 'loading'
    },

    get items(): BundleItemAvailability[] {
      return getItems()
    },

    getItem(bundleItemId: string): BundleItemAvailability | undefined {
      return getItems().find((item) => item.bundleItemId === bundleItemId)
    },

    // Asks again right now, ignoring the failure backoff.
    retry(): Promise<void> {
      const request = currentRequest()

      return request ? cache.forceRevalidate(request.key, request.dims) : Promise.resolve()
    },

    // Entries stop being served, the next read fetches like a first load, and any in-flight fetch is
    // superseded.
    invalidate(): Promise<void> {
      cache.invalidate({ mode: 'hard' })

      const request = currentRequest()

      return request && cache.isFetching(request.key) ? cache.forceRevalidate(request.key, request.dims) : Promise.resolve()
    }
  }
}

declare global {
  export type BundleAvailability = ReturnType<typeof createBundleAvailability>
}
